import React, {FC, useEffect, useState} from 'react';
import {Table, TableProps} from "antd";
import { useTranslation } from 'react-i18next';

import { getProviderMapping } from '../service/SummaryService';

import {ProviderMapping} from '../model/ProviderMappingResponse';

//
// Structure to hold the flattened provider mapping data
//
interface IndividualMapping {
  key: number;
  origin: string;
  originCount: number;
  externalProvider: string;
  externalProviderCount: number;
  internalProvider: string;
  engine: string;
}

const ProviderSecurityMapping: FC<{ accessToken: string }> = ({ accessToken }) => {
  const [providerMappings, setProviderMappings] = useState<IndividualMapping[]>();

  const { t } = useTranslation();

  // Capitalise Origin names where appropriate
  const capitaliseOrigin = (name: string): string => {
    if (!name || name.length === 0 || name.startsWith("http")) return name;
    return name.substring(0, 1).toUpperCase() + name.substring(1,);
  };

  // Flatten the nested mapping data into individual rows for display in a table, and count row spans.
  // Cells with a rowSpan of zero are not displayed. This causes problems with paging where the spanning cell from a
  // previous page is not displayed and the columns are out of step.
  const flattenMappingData = (mappings: ProviderMapping[]): IndividualMapping[] => {
    const individualMappings: IndividualMapping[] = [];
    let rowKey = 0; // each row needs to have a unique key

    let originRowspan = 0;
    let firstOrigin = rowKey;
    let prevOrigin = '';

    mappings.forEach(mapping => {
      if (mapping.origin !== prevOrigin) {
        firstOrigin = rowKey;
        prevOrigin = mapping.origin;
        originRowspan = 0;
      }

      if (mapping.internalProviders.length == 0) {
        individualMappings.push({
          key: rowKey,
          origin: capitaliseOrigin(mapping.origin),
          originCount: 0,
          externalProvider: mapping.externalProvider,
          externalProviderCount: 1,
          internalProvider: '',
          engine: ''
        });
        rowKey++;
        originRowspan++;
      }
      else {
        let externalRowSpan = 1;
        let firstMapping = rowKey;
        mapping.internalProviders.forEach(internal => {
          individualMappings.push({
            key: rowKey,
            origin: capitaliseOrigin(mapping.origin),
            originCount: 0,
            externalProvider: mapping.externalProvider,
            externalProviderCount: 0,
            internalProvider: internal.name,
            engine: internal.rulesEngineUrl
          })
          // Span the external provider over all of its internal mapping rows
          individualMappings[firstMapping].externalProviderCount = externalRowSpan++;
          rowKey++;
          originRowspan++
        })
      }

      // Span the origin over all of its entries
      individualMappings[firstOrigin].originCount = originRowspan;
    });
    return individualMappings;
  };

  useEffect(
    () => {
      getProviderMapping(accessToken, t).then(
        mappings => setProviderMappings(flattenMappingData(mappings.data.providerMappings))
      );
    },
    []
  );

  const columns: TableProps<IndividualMapping>['columns']  = [
    {
      title: t('provider.security.origin'),
      dataIndex: 'origin',
      onCell: (mapping, _) => ({ rowSpan: mapping.originCount})
    },
    {
      title: t('provider.security.external.provider'),
      dataIndex: 'externalProvider',
      onCell: (mapping, _) => ({ rowSpan: mapping.externalProviderCount})
    },
    {
      title: t('provider.security.internal.provider.name'),
      dataIndex: 'internalProvider'
    },
    {
      title: t('provider.security.internal.provider.url'),
      dataIndex: 'engine'
    },
  ];

  return (
      <Table
        className="enquiry-table"
        rowKey="key"
        columns={columns}
        dataSource={providerMappings}
        pagination={false}
      />
  );
};

export default ProviderSecurityMapping;
